/**
 * Shared browser → S3 presigned-PUT helpers. Extracted from
 * ProtocolUploadCard (DREAM-19) so protocol amendments and other
 * presign/finalise flows use the exact same integrity + encryption path.
 */

export function guessMime(name: string): string {
  const ext = name.toLowerCase().split('.').pop();
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'docx') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  return 'application/octet-stream';
}

/**
 * Compute SHA-256 of a File using Web Crypto. crypto.subtle is only
 * available in secure contexts (HTTPS or http://localhost); falls back to
 * null and lets the BE rely on its server-side checksum.
 */
export async function sha256OfFile(file: File): Promise<string | null> {
  if (typeof crypto === 'undefined' || !crypto.subtle) return null;
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** PUT with progress via XMLHttpRequest (fetch has no upload-progress event yet). */
export function uploadWithProgress(
  url: string,
  file: File,
  contentType: string,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    // BE presigns with ServerSideEncryption=AES256 in SignedHeaders, so the
    // browser must echo the same header back or S3 rejects with 403.
    xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
      } else {
        reject(new Error(`S3 PUT failed: ${xhr.status} ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}
